import { Injectable, inject } from '@angular/core';
import { lastValueFrom, timeout } from 'rxjs';
import { RestService, SecureStorageService } from '@copec-gid/gid-arquitectura.seguridad-autorizacion-modulo';
import { EndpointResource } from '../core/conf/endpoint_conf';
import { LIB_HEADERS } from '../core/conf/lib_conf';
import { Globals } from '../globals';

interface SessionPostResponse {
  data?: {
    sesion_token?: string;
    sesion_key?: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class SessionService {

  private readonly rest = inject(RestService);
  private readonly storage = inject(SecureStorageService);

  // Mismas claves que deriva AuthLibService, que es quien las lee al firmar.
  private readonly sessionTokenKey = this.storage.encrypt('session_token', true);
  private readonly sessionKeyKey = this.storage.encrypt('session_key', true);

  getSessionToken(): string | null {
    return this.storage.getSignal<string>(this.sessionTokenKey)();
  }

  getSessionKey(): string | null {
    return this.storage.getSignal<string>(this.sessionKeyKey)();
  }

  /*
    Solo la key: en modo cookies el token de sesión viaja en la cookie HttpOnly
    `webToken` y el backend ya no lo devuelve en el body. Exigir ambos dejaba la
    sesión siempre por establecer.
  */
  hasExistingSession(): boolean {
    return !!this.getSessionKey();
  }

  async createNoLoginSession(): Promise<void> {
    const response = await lastValueFrom(
      this.rest.post<SessionPostResponse>(
        EndpointResource.sessionEndpoint,
        { session_type: 'create_session' },
        { isInitSession: true, headers: LIB_HEADERS }
      ).pipe(timeout(Globals.apiTimeout))
    );

    const sessionKey = response?.data?.sesion_key;

    if (!sessionKey) {
      return;
    }

    this.storage.setItem(this.sessionKeyKey, sessionKey);

    // Hoy el backend no lo manda (va en la cookie); se guarda si algún día vuelve.
    const sessionToken = response?.data?.sesion_token;
    if (sessionToken) {
      this.storage.setItem(this.sessionTokenKey, sessionToken);
    }
  }
}
